import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SetUpTeamAlertTemplatesResult,
  TeamAlertDelivery,
  TeamAlertRecipient,
  TeamAlertRecipientInput,
  TeamAlertsOverview,
  UpdateTeamAlertRecipientInput,
  UpdateTeamAlertSettingsInput,
} from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** Settings > WhatsApp alerts for my team (ADR-039). */

const KEY = ['team-alerts'] as const;

export function useTeamAlerts() {
  return useQuery<TeamAlertsOverview, ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<TeamAlertsOverview>('/team-alerts'),
  });
}

export function useUpdateTeamAlertSettings() {
  const qc = useQueryClient();
  return useMutation<TeamAlertsOverview, ApiClientError, UpdateTeamAlertSettingsInput>({
    mutationFn: (input) => apiClient.put<TeamAlertsOverview>('/team-alerts/settings', input),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export function useSetUpTeamAlertTemplates() {
  const qc = useQueryClient();
  return useMutation<SetUpTeamAlertTemplatesResult, ApiClientError, void>({
    mutationFn: () => apiClient.post<SetUpTeamAlertTemplatesResult>('/team-alerts/templates'),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddTeamAlertRecipient() {
  const qc = useQueryClient();
  return useMutation<TeamAlertRecipient, ApiClientError, TeamAlertRecipientInput>({
    mutationFn: (input) => apiClient.post<TeamAlertRecipient>('/team-alerts/recipients', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTeamAlertRecipient() {
  const qc = useQueryClient();
  return useMutation<
    TeamAlertRecipient,
    ApiClientError,
    { id: string; input: UpdateTeamAlertRecipientInput }
  >({
    mutationFn: ({ id, input }) =>
      apiClient.patch<TeamAlertRecipient>(`/team-alerts/recipients/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveTeamAlertRecipient() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => apiClient.delete<void>(`/team-alerts/recipients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSendTeamAlertTest() {
  const qc = useQueryClient();
  return useMutation<TeamAlertDelivery, ApiClientError, string>({
    mutationFn: (id) => apiClient.post<TeamAlertDelivery>(`/team-alerts/recipients/${id}/test`),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
